import type { Me, PermissionKey, TokenGrant } from '@sentinel/shared';
import { create } from 'zustand';
import { installTokenProvider } from '../api/client';
import { authEndpoints } from '../api/auth-endpoints';
import { queryClient } from '../query/query-client';

export type AuthStatus = 'booting' | 'anonymous' | 'authenticated';

interface AuthState {
  status: AuthStatus;
  accessToken: string | null;
  expiresAt: number | null;
  me: Me | null;
  /** Applies a token grant (login, TOTP, organization switch, platform entry). */
  applyGrant: (grant: TokenGrant) => void;
  setMe: (me: Me) => void;
  /** Tries the refresh cookie at boot; resolves to the final status. */
  bootstrap: () => Promise<AuthStatus>;
  refresh: () => Promise<string | null>;
  logout: () => Promise<void>;
  clear: () => void;
}

const REFRESH_MARGIN_MS = 60_000;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let refreshInFlight: Promise<string | null> | null = null;
let bootstrapInFlight: Promise<AuthStatus> | null = null;

/** Who and where the data on screen belong to: user, context and organization. */
function scopeOf(me: Me | null): string | null {
  return me === null ? null : `${me.id}:${me.context}:${me.organization?.id ?? '-'}`;
}

function scheduleRefresh(expiresIn: number, refresh: () => Promise<string | null>): void {
  if (refreshTimer !== null) clearTimeout(refreshTimer);
  const delay = Math.max(5_000, expiresIn * 1000 - REFRESH_MARGIN_MS);
  refreshTimer = setTimeout(() => {
    void refresh();
  }, delay);
}

/** Access token in memory only; the refresh token never leaves its httpOnly cookie. */
export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'booting',
  accessToken: null,
  expiresAt: null,
  me: null,

  applyGrant(grant) {
    // Another organization, context or person: nothing cached may reach the new screens.
    const previous = scopeOf(get().me);
    if (previous !== null && previous !== scopeOf(grant.me)) queryClient.clear();
    set({
      status: 'authenticated',
      accessToken: grant.accessToken,
      expiresAt: Date.now() + grant.expiresIn * 1000,
      me: grant.me,
    });
    scheduleRefresh(grant.expiresIn, get().refresh);
  },

  setMe(me) {
    set({ me });
  },

  async bootstrap() {
    // React StrictMode mounts effects twice in development: one restore per page load.
    bootstrapInFlight ??= (async () => {
      const token = await get().refresh();
      if (token === null) {
        set({ status: 'anonymous' });
        return 'anonymous' as const;
      }
      try {
        const me = await authEndpoints.me();
        set({ status: 'authenticated', me });
        return 'authenticated' as const;
      } catch {
        get().clear();
        return 'anonymous' as const;
      }
    })().finally(() => {
      bootstrapInFlight = null;
    });
    return bootstrapInFlight;
  },

  async refresh() {
    // Single flight: concurrent callers share one rotation, never presenting a used token twice.
    refreshInFlight ??= authEndpoints
      .refresh()
      .then((result) => {
        set({ accessToken: result.accessToken, expiresAt: Date.now() + result.expiresIn * 1000 });
        scheduleRefresh(result.expiresIn, get().refresh);
        return result.accessToken;
      })
      .catch(() => null)
      .finally(() => {
        refreshInFlight = null;
      });
    return refreshInFlight;
  },

  async logout() {
    try {
      await authEndpoints.logout();
    } catch {
      /* the session may already be gone; the local state is cleared anyway */
    }
    get().clear();
  },

  clear() {
    if (refreshTimer !== null) clearTimeout(refreshTimer);
    refreshTimer = null;
    // A shared operations-room computer: the next person must not see this one's data.
    queryClient.clear();
    set({ status: 'anonymous', accessToken: null, expiresAt: null, me: null });
  },
}));

installTokenProvider({
  getAccessToken: () => useAuthStore.getState().accessToken,
  refresh: () => useAuthStore.getState().refresh(),
  onSessionLost: () => useAuthStore.getState().clear(),
});

/** UI-side permission check: hides and disables only; the server re-checks every call. */
export function useCan(permission: PermissionKey): boolean {
  return useAuthStore((s) => s.me?.permissions.includes(permission) ?? false);
}

export function useMe(): Me | null {
  return useAuthStore((s) => s.me);
}
